import { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { I18nextProvider } from 'react-i18next';
import i18n from './i18n';
import { AuthProvider } from './contexts/AuthContext';
import { CurrencyProvider } from './contexts/CurrencyContext';
import "@/App.css";
// Eagerly-loaded landing route — first paint
import HomePage from './pages/HomePage';
import { Toaster } from './components/ui/sonner';

// Lazy-load every other route to keep initial bundle small
const ProductsPage = lazy(() => import('./pages/ProductsPage'));
const ProductDetailPage = lazy(() => import('./pages/ProductDetailPage'));
const CartPage = lazy(() => import('./pages/CartPage'));
const CheckoutPage = lazy(() => import('./pages/CheckoutPage'));
const OrderSuccessPage = lazy(() => import('./pages/OrderSuccessPage'));
const OrdersPage = lazy(() => import('./pages/OrdersPage'));
const AdminPage = lazy(() => import('./pages/AdminPage'));
const AdminLoginPage = lazy(() => import('./pages/AdminLoginPage'));
const LoginPage = lazy(() => import('./pages/LoginPage'));
const RegisterPage = lazy(() => import('./pages/RegisterPage'));
const ProfilePage = lazy(() => import('./pages/ProfilePage'));
const WishlistPage = lazy(() => import('./pages/WishlistPage'));
const ContactPage = lazy(() => import('./pages/ContactPage'));
const TermsPage = lazy(() => import('./pages/TermsPage'));
const TicketsPage = lazy(() => import('./pages/TicketsPage'));
const AboutPage = lazy(() => import('./pages/AboutPage'));
const AffiliateRegisterPage = lazy(() => import('./pages/AffiliateRegisterPage'));
const AffiliateLoginPage = lazy(() => import('./pages/AffiliateLoginPage'));
const AffiliateDashboardPage = lazy(() => import('./pages/AffiliateDashboardPage'));
// Defer chatbot — non-critical, idle
const ChatBot = lazy(() => import('./components/ChatBot'));
const PWAInstallPrompt = lazy(() => import('./components/PWAInstallPrompt'));
const PublicShell = lazy(() => import('./components/PublicShell'));

const RouteFallback = () => (
  <div className="min-h-screen bg-[#F9F8F6] flex items-center justify-center" data-testid="route-loading">
    <div className="w-10 h-10 rounded-full border-2 border-[#D96C4A] border-t-transparent animate-spin" />
  </div>
);

function App() {
  return (
    <I18nextProvider i18n={i18n}>
      <AuthProvider>
        <CurrencyProvider>
        <div className="App">
          <BrowserRouter>
            <Suspense fallback={<RouteFallback />}>
              <Routes>
                <Route path="/" element={<PublicShell><HomePage /></PublicShell>} />
                <Route path="/products" element={<PublicShell><ProductsPage /></PublicShell>} />
                <Route path="/products/:id" element={<PublicShell><ProductDetailPage /></PublicShell>} />
                {/* SEO-friendly localized URLs — e.g. /english/products/digital-products/ai-ebooks */}
                <Route path="/:langSlug/products" element={<PublicShell><ProductsPage /></PublicShell>} />
                <Route path="/:langSlug/products/:category" element={<PublicShell><ProductsPage /></PublicShell>} />
                <Route path="/:langSlug/products/:category/:subcategory" element={<PublicShell><ProductsPage /></PublicShell>} />
                <Route path="/:langSlug/product/:id" element={<PublicShell><ProductDetailPage /></PublicShell>} />
                <Route path="/cart" element={<PublicShell><CartPage /></PublicShell>} />
                <Route path="/checkout" element={<CheckoutPage />} />
                <Route path="/order-success" element={<PublicShell><OrderSuccessPage /></PublicShell>} />
                <Route path="/orders" element={<PublicShell><OrdersPage /></PublicShell>} />
                <Route path="/admin" element={<AdminPage />} />
                <Route path="/admin/access" element={<AdminLoginPage />} />
                <Route path="/login" element={<LoginPage />} />
                <Route path="/register" element={<RegisterPage />} />
                <Route path="/profile" element={<PublicShell><ProfilePage /></PublicShell>} />
                <Route path="/wishlist" element={<PublicShell><WishlistPage /></PublicShell>} />
                <Route path="/contact" element={<PublicShell><ContactPage /></PublicShell>} />
                <Route path="/terms" element={<PublicShell><TermsPage /></PublicShell>} />
                <Route path="/affiliate-terms" element={<PublicShell><TermsPage contentKey="affiliate-terms" /></PublicShell>} />
                <Route path="/tickets" element={<PublicShell><TicketsPage /></PublicShell>} />
                <Route path="/about" element={<PublicShell><AboutPage /></PublicShell>} />
                <Route path="/affiliate" element={<PublicShell><AffiliateRegisterPage /></PublicShell>} />
                <Route path="/affiliate/register" element={<PublicShell><AffiliateRegisterPage /></PublicShell>} />
                <Route path="/affiliate/login" element={<AffiliateLoginPage />} />
                <Route path="/affiliate/dashboard" element={<PublicShell><AffiliateDashboardPage /></PublicShell>} />
              </Routes>
            </Suspense>
          </BrowserRouter>
          <Suspense fallback={null}>
            <ChatBot />
            <PWAInstallPrompt />
          </Suspense>
          <Toaster />
        </div>
        </CurrencyProvider>
      </AuthProvider>
    </I18nextProvider>
  );
}

export default App;
